/**
 * Onboarding flow state.
 *
 * Deliberately NOT in the global Redux store, which is a divergence from the
 * rest of this codebase and worth stating plainly.
 *
 * This state is ephemeral and scoped to one journey. In the global store it
 * would outlive the flow, need manual teardown on every exit path, and sit
 * alongside auth and session state that three other features read — so a stray
 * selector could couple the app to a half-finished signup. A reducer scoped to
 * the route is created and destroyed with the flow, which is the correct
 * lifetime for it.
 *
 * It is persisted to sessionStorage instead, so a refresh mid-flow does not
 * discard ten screens of input, and cleared on completion. sessionStorage
 * rather than localStorage: an abandoned signup should not still be sitting
 * there next week.
 */

/** @type {import('../steps/registry').FlowState} */
export const initialFlowState = {
  pathway: null,
  existingAccount: false,
  dob: null,
  email: null,
  emailVerified: false,
  goals: [],
  goalData: {},
  // Policy id -> { version, acceptedAt }. Persisted so a refresh can restore
  // an acknowledgment only when it matches every currently published revision.
  //
  // The password is deliberately NOT here. Flow state is written to
  // sessionStorage on every change, and a password sitting in storage for the
  // life of the tab is not worth the convenience of surviving a refresh.
  consents: {},
  profile: {},
  demographics: {},
  // Set when Done opens, because arriving there is what sends the completion.
  // The route guard reads it to keep the browser's Back out of answers already
  // with the server. Persisted like everything else here, which is what makes
  // it survive the provider remounting on every navigation.
  //
  // Cleared again when completion is refused for something the member can fix,
  // because the answers are not with the server after all.
  submitted: false,
  // Set once the server has accepted the completion. Never persisted: the
  // provider clears storage for a completed flow rather than writing it, which
  // is what keeps the teardown from racing a pending `submitted` write.
  completed: false,
};

export const FlowActions = {
  SET_PATHWAY: "SET_PATHWAY",
  SET_DOB: "SET_DOB",
  SET_EMAIL: "SET_EMAIL",
  SET_EMAIL_VERIFIED: "SET_EMAIL_VERIFIED",
  SET_CONSENTS: "SET_CONSENTS",
  SET_GOALS: "SET_GOALS",
  MERGE_GOAL_DATA: "MERGE_GOAL_DATA",
  MERGE_PROFILE: "MERGE_PROFILE",
  SET_DEMOGRAPHICS: "SET_DEMOGRAPHICS",
  SET_SUBMITTED: "SET_SUBMITTED",
  SET_COMPLETED: "SET_COMPLETED",
  RESET: "RESET",
};

/**
 * Order-independent goal comparison. `GoalsStep.toggle` appends, so the same
 * two goals can arrive in either order, and re-submitting an unchanged
 * selection must not read as a change.
 */
const sameGoals = (a = [], b = []) => {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && [...left].every((g) => right.has(g));
};

/**
 * Later steps depend on earlier answers, so changing an earlier answer must
 * invalidate what was derived from it. Without this, editing your date of
 * birth after selecting goals leaves goal data attached to a flow that may no
 * longer be reachable — the classic stale-wizard bug.
 */
export const flowReducer = (state, action) => {
  // For locally debugging reducer actions uncomment the following:
  // console.log('flowReducer', state, action);

  switch (action.type) {
    case FlowActions.SET_PATHWAY:
      // Switching pathway invalidates everything downstream of it.
      if (state.pathway === action.pathway) return state;
      return { ...initialFlowState, pathway: action.pathway };

    case FlowActions.SET_DOB:
      return { ...state, dob: action.dob };

    case FlowActions.SET_EMAIL:
      // A different email means the previous verification no longer applies.
      if (state.email === action.email) return state;
      return { ...state, email: action.email, emailVerified: false };

    case FlowActions.SET_EMAIL_VERIFIED:
      return { ...state, emailVerified: action.verified === true };

    case FlowActions.SET_CONSENTS:
      return { ...state, consents: action.consents };

    case FlowActions.SET_GOALS: {
      const goals = action.goals ?? [];
      // `GoalsStep` dispatches on every Continue, changed or not, so without
      // this guard merely revisiting the goal screen would discard the answers.
      if (sameGoals(goals, state.goals)) return state;
      // Every sub-flow's questions are a function of the whole set, not of its
      // own goal: `planSubFlows` moves a question from one sub-flow to another
      // depending on what else was chosen. So a change to the set invalidates
      // every answer, including those for goals that stayed selected — keeping
      // them would leave one question answered twice under two goals.
      //
      // The cost is that adding a goal re-walks the sub-flows already done.
      // Accepted: the alternative is diffing each goal's `asks` against the old
      // plan, which is a second place the dedup rules would live.
      return { ...state, goals, goalData: {} };
    }

    case FlowActions.MERGE_GOAL_DATA:
      return {
        ...state,
        goalData: { ...state.goalData, ...action.data },
      };

    case FlowActions.MERGE_PROFILE:
      return { ...state, profile: { ...state.profile, ...action.profile } };

    case FlowActions.SET_DEMOGRAPHICS:
      // Replaces rather than merges: skipping after answering must clear the
      // answers, not leave them silently attached.
      return { ...state, demographics: action.demographics ?? {} };

    case FlowActions.SET_SUBMITTED:
      return { ...state, submitted: action.submitted === true };

    case FlowActions.SET_COMPLETED:
      return { ...state, completed: true };

    case FlowActions.RESET:
      return initialFlowState;

    default:
      return state;
  }
};
